import * as React from 'react';
import { observer } from 'mobx-react';
import { useStores } from 'store';
import { AdvanceTable, DangerTag, IBasicTableColumn, notification, Spinner, SuccessTag, Typography } from 'ui-lib';
import { TableUtils } from 'pages/private/components/table-utils';
import { AccountType, IAccount } from 'dto/access-management/account-dto';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { useAccountTreeSelector } from 'hooks/accounts/account-datasource.hook';
import { useNavigate } from 'react-router-dom';
import { buildNavigatePath } from 'pages/private/utils';
import { WelcomePackDto, WelcomePackStatus } from 'dto/welcome-pack.dto';
import { useLoadingStates } from 'hooks/use-loading.hook';
import welcomePackCss from './welcome-pack.css';
import { WelcomePackUtils } from './utils';
import { useUrlSearch } from 'hooks/use-search.hook';
import { getErrorMessage } from 'common-utils';

export const WelcomePackConfigurationsListing = observer(() => {
    const { welcomePackStore } = useStores();
    const navigate = useNavigate();
    const { getSearchParam, updateSearchParams } = useUrlSearch();
    const { loading, saving, withLoading, withSaving } = useLoadingStates();

    const selector = useAccountTreeSelector({
        selectableTypes: [AccountType.CLIENT],
        defaultSelectedAccount: getSearchParam('accountUuid'),
        onAccountChange: async (account: IAccount) => {
            updateSearchParams({ accountUuid: account?.uuid || null });
            if (!account) {
                welcomePackStore.clearEntities();
                return;
            }
            await withLoading(async () => {
                return welcomePackStore.getWelcomePacks(account.id, account.uuid);
            });
        },
    });

    const allowReload = React.useMemo(() => {
        if (loading) {
            return false;
        }
        return !!selector.selectedAccount;
    }, [loading, selector.selectedAccount]);

    const allowAddNew = React.useMemo(() => {
        if (!allowReload) {
            return false;
        }
        return welcomePackStore.welcomePacks.every((item) => !WelcomePackUtils.isActive(item));
    }, [allowReload, welcomePackStore.welcomePacks]);

    const columns: IBasicTableColumn[] = [
        TableUtils.createBasicFilterColumn({
            key: 'label',
            title: 'Label',
            columnExtraOptions: {
                render: (text: string, record: WelcomePackDto) => (
                    <Typography.Link
                        onClick={() =>
                            navigate({
                                pathname: buildNavigatePath('/devices/welcome-pack'),
                                search: `accountUuid=${record.accountUuid}&id=${record.id}`,
                            })
                        }
                    >
                        {text}
                    </Typography.Link>
                ),
            },
        }),
        TableUtils.createBasicEnumFilterColumn({
            key: 'status',
            title: 'Status',
            filters: [
                { text: WelcomePackStatus.ACTIVE, value: WelcomePackStatus.ACTIVE },
                { text: WelcomePackStatus.INACTIVE, value: WelcomePackStatus.INACTIVE },
            ],
            columnExtraOptions: {
                render: (text: string, record: WelcomePackDto) => {
                    if (WelcomePackUtils.isActive(record)) {
                        return <SuccessTag value={record.status} />;
                    }
                    return <DangerTag value={record.status} />;
                },
            },
        }),
        TableUtils.createBasicFilterTimeColumn({ key: 'createdAt', title: 'Created At' }),
        TableUtils.createExtraColumn({
            key: 'actions',
            title: 'Actions',
            render: (text: string, record: WelcomePackDto) => (
                <div key={record.id}>
                    <Typography.Link
                        hidden={!WelcomePackUtils.isActive(record)}
                        style={{ marginRight: '10px' }}
                        onClick={() =>
                            navigate({
                                pathname: buildNavigatePath('/devices/welcome-pack-tracking'),
                                search: `accountUuid=${record.accountUuid}&id=${record.id}`,
                            })
                        }
                    >
                        Tracking
                    </Typography.Link>
                    <Typography.Link
                        disabled={!WelcomePackUtils.isActive(record)}
                        onClick={async () => {
                            WelcomePackUtils.showDeactivateConfirmDialog(async (force?: boolean) => {
                                try {
                                    const result = await withSaving(async () => {
                                        return welcomePackStore.inactivateWelcomePack(
                                            selector.selectedAccount?.id,
                                            record?.accountUuid,
                                            record?.id,
                                            force
                                        );
                                    });
                                    if (result.getOrThrow()) {
                                        notification.success({
                                            message: 'Deactivate Welcome Pack',
                                            description: `The welcome pack ${record?.label} has been deactivated.`,
                                        });
                                    }
                                } catch (err) {
                                    notification.error({
                                        message: 'Error',
                                        description: getErrorMessage(err),
                                    });
                                }
                            });
                        }}
                    >
                        Deactivate
                    </Typography.Link>
                </div>
            ),
        }),
    ];

    return (
        <Spinner spinning={saving} className={welcomePackCss['loading-spinner']}>
            <Typography.Text>The inventory lists all the welcome packs that you can manage.</Typography.Text>
            <div>
                <OrganisationSelect
                    placeholder='Organisation'
                    treeDataSource={selector.dataSource}
                    filterTreeNode={(input, treeNode) => {
                        return treeNode.label?.includes(input);
                    }}
                    expandAll={true}
                    onChange={selector.onChange}
                    value={selector.selectedAccount?.id}
                />
            </div>
            <AdvanceTable
                title='Welcome Packs'
                loadingState={loading}
                table={{
                    columns,
                    dataSource: welcomePackStore.welcomePacks,
                    rowKey: 'id',
                }}
                toolbar={{
                    onAdd: allowAddNew
                        ? () => {
                              navigate({
                                  pathname: buildNavigatePath('/devices/welcome-pack'),
                                  search: selector.selectedAccount
                                      ? `accountUuid=${selector.selectedAccount.uuid}`
                                      : null,
                              });
                          }
                        : null,
                    onReload: allowReload
                        ? () => {
                              withLoading(async () => {
                                  return welcomePackStore.getWelcomePacks(
                                      selector.selectedAccount?.id,
                                      selector.selectedAccount?.uuid,
                                      true,
                                  );
                              });
                          }
                        : null,
                }}
            />
        </Spinner>
    );
});
